/**
 * Catalog item types.
 *
 * This is the source-of-truth list used to seed the `item_types` reference
 * table (see scripts/seeds/01_item_types.ts). Each entry has a stable,
 * const-style `slug` (used in code and as the FK value on paintings) and a
 * human-readable `name` for the interface.
 *
 * The database table is authoritative at runtime (the UI fetches it via
 * `trpc.itemTypes.list`), but this const keeps slugs type-safe and gives us a
 * sensible fallback ordering.
 */
export const ITEM_TYPES = [
  { slug: "artwork", name: "Artwork", catalogPrefix: "UHBG", sortOrder: 0 },
  { slug: "clothing", name: "Clothing", catalogPrefix: "UHBC", sortOrder: 1 },
  { slug: "souvenir", name: "Souvenir", catalogPrefix: "UHBS", sortOrder: 2 },
] as const;

export type ItemTypeSlug = (typeof ITEM_TYPES)[number]["slug"];

/** Default type applied to existing items and new artworks. */
export const DEFAULT_ITEM_TYPE: ItemTypeSlug = "artwork";

/** Return the configured public catalog prefix for an item type. */
export function catalogPrefixFor(slug: string | null | undefined): string | null {
  return ITEM_TYPES.find((type) => type.slug === (slug || DEFAULT_ITEM_TYPE))
    ?.catalogPrefix ?? null;
}

/** Resolve a public catalog prefix back to its item type. */
export function itemTypeForCatalogPrefix(prefix: string): ItemTypeSlug | null {
  return ITEM_TYPES.find(
    (type) => type.catalogPrefix.toLowerCase() === prefix.toLowerCase()
  )?.slug ?? null;
}

/**
 * Fields that only apply to artworks. For other item types (clothing,
 * souvenirs) these are hidden in the entry forms and skipped on detail views.
 */
export const ARTWORK_ONLY_FIELDS = [
  "artistName",
  "artistAge",
  "medium",
  "style",
  "location",
  "teacher",
  "school",
  "colors",
] as const;

/** Whether the given item type should show artwork-specific fields. */
export function isArtworkType(slug: string | null | undefined): boolean {
  return !slug || slug === "artwork";
}
